import { api } from "@/lib/api";

export type SettingSource = "db" | "env";

export interface TmdbStatus {
  configured: boolean;
  source: SettingSource | null;
  updated_at: string | null;
}

export interface TestResult {
  ok: boolean;
  message: string | null;
}

/**
 * Per-media-kind category names. The strings are passed verbatim to
 * whichever download client picks up the grab (embedded NZB, NZBGet,
 * SAB, qBit). Empty = "let the client use its default category".
 */
export interface DownloadCategories {
  movies: string;
  tv: string;
  music: string;
}

/**
 * Per-media-type pause switch. `false` stops new automatic grabs and
 * the one-click search for that media type; interactive search and
 * downloads already in the client are unaffected.
 */
export interface DownloadsEnabled {
  movies: boolean;
  tv: boolean;
  music: boolean;
}

export type DownloadsKind = keyof DownloadsEnabled;

/**
 * Cross-connector toggles for the notifications subsystem (issue #40).
 * Per-event toggles on individual connectors (Discord/ntfy/webhook/Plex)
 * still apply on top of these — flipping `notify_on_indexer_failure`
 * to `false` is the kill switch for the indexer-disabled alert across
 * every configured connector.
 */
export interface NotificationSettings {
  notify_on_indexer_failure: boolean;
  notify_on_indexer_recovery: boolean;
}

/**
 * Issue #25: importer chmod / chown configuration.
 *
 * All four fields are free-form strings rather than typed numbers
 * because the empty string is the canonical "unset" state — meaning
 * "leave the file with whatever mode/owner the OS produced", which is
 * the pre-#25 default behaviour. Mode strings are octal (`"644"`,
 * `"0o755"`); uid/gid are decimal.
 *
 * On Windows the API still accepts these values but the importer
 * skips applying them — POSIX modes / numeric uid don't translate to
 * Windows ACLs.
 */
export interface ImporterPermsSettings {
  /** Octal mode applied to placed files (e.g. "644"). Empty = no chmod. */
  file_mode: string;
  /** Octal mode applied to created dirs (e.g. "755"). Empty = no chmod. */
  dir_mode: string;
  /** Decimal uid for chown. Empty = no chown. */
  uid: string;
  /** Decimal gid for chown. Empty = no chown. */
  gid: string;
}

export const settingsApi = {
  tmdb: {
    get: () => api<TmdbStatus>("/api/v1/settings/tmdb"),
    set: (api_key: string) =>
      api<TmdbStatus>("/api/v1/settings/tmdb", {
        method: "PUT",
        body: JSON.stringify({ api_key }),
      }),
    clear: () => api<void>("/api/v1/settings/tmdb", { method: "DELETE" }),
    /**
     * Test a key. Pass `api_key` to test an unsaved value (test-before-save);
     * omit it to test the currently-configured key.
     */
    test: (api_key?: string) =>
      api<TestResult>("/api/v1/settings/tmdb/test", {
        method: "POST",
        body: JSON.stringify(api_key ? { api_key } : {}),
      }),
  },
  downloadCategories: {
    get: () => api<DownloadCategories>("/api/v1/settings/download-categories"),
    set: (body: DownloadCategories) =>
      api<DownloadCategories>("/api/v1/settings/download-categories", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
  downloadsEnabled: {
    get: () => api<DownloadsEnabled>("/api/v1/settings/downloads-enabled"),
    set: (body: Partial<DownloadsEnabled>) =>
      api<DownloadsEnabled>("/api/v1/settings/downloads-enabled", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
  notifications: {
    get: () => api<NotificationSettings>("/api/v1/settings/notifications"),
    set: (body: NotificationSettings) =>
      api<NotificationSettings>("/api/v1/settings/notifications", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
  importerPerms: {
    get: () => api<ImporterPermsSettings>("/api/v1/settings/importer-perms"),
    set: (body: ImporterPermsSettings) =>
      api<ImporterPermsSettings>("/api/v1/settings/importer-perms", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
};
